import { IconQuestionMarkCircleFilled } from '@zillow/constellation-icons/react/filled/icon-question-mark-circle-filled';
import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { Icon } from '../icon/icon';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import {
  triggerButtonDefaultIconStyles,
  triggerButtonStyles,
} from './styles/trigger-button.styles';
import type { TriggerButtonPropsInterface } from './types/trigger-button.types';

/**
 * A standalone component for opening `Tooltip` and `Popover` components.
 *
 * The default child for `Popover.Trigger` and `Tooltip.Trigger`.
 *
 * @related ['Popover', 'Tooltip', 'TriggerText']
 * @since 7.0.0
 */

export const TriggerButton = forwardRef<HTMLButtonElement, TriggerButtonPropsInterface>(
  (props: TriggerButtonPropsInterface, forwardedRef) => {
    const { className, css: cssProp, icon, ...rest } = props;
    const content = icon ?? <IconQuestionMarkCircleFilled />;
    const styles = css(
      triggerButtonStyles,
      icon ? undefined : triggerButtonDefaultIconStyles,
      cssProp,
    );

    return (
      <button
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="TriggerButton"
        className={cx('c11n', styles, className)}
        type="button"
        {...rest}
      >
        <Icon render={content} size="sm" />
        <VisuallyHidden>Help</VisuallyHidden>
      </button>
    );
  },
);

TriggerButton.displayName = 'TriggerButton';
